import React from 'react';
import { motion } from 'framer-motion';

function WealthManagement() {
  const services = [
    {
      title: 'Portfolio Optimization',
      description: 'Strategic asset allocation designed to maximize returns while managing risk according to your specific objectives and constraints.',
      features: ['Risk-Adjusted Returns', 'Diversification Strategy', 'Rebalancing', 'Performance Monitoring']
    },
    {
      title: 'Risk Assessment',
      description: 'Comprehensive risk analysis and management framework to protect your wealth across market cycles.',
      features: ['Market Risk Analysis', 'Credit Risk Evaluation', 'Liquidity Management', 'Stress Testing']
    },
    {
      title: 'Tax Efficiency',
      description: 'Advanced tax planning strategies to minimize tax burden and maximize after-tax returns.',
      features: ['Tax-Loss Harvesting', 'Asset Location', 'Estate Planning', 'Charitable Giving']
    },
    {
      title: 'Estate Planning',
      description: 'Comprehensive estate planning solutions to preserve and transfer wealth across generations.',
      features: ['Trust Structures', 'Succession Planning', 'Asset Protection', 'Legacy Planning']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Assessment',
      description: 'We begin with a comprehensive review of your financial situation, goals, and risk tolerance.'
    },
    {
      step: '02',
      title: 'Strategy Development',
      description: 'Custom investment strategy tailored to your unique circumstances and objectives.'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Professional execution of your wealth management plan with ongoing monitoring.'
    },
    {
      step: '04',
      title: 'Review & Optimization',
      description: 'Regular reviews and adjustments to ensure your strategy remains aligned with your goals.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <img 
          src="/new-assets/pexels-karola-g-7876662.jpg"
          alt="Wealth Management"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-[-0.02em] mb-6">
              <span className="block font-light">Wealth</span>
              <span className="block font-medium text-[#fbbf24]">Management</span>
            </h1>
            <p className="text-white/90 text-xl sm:text-2xl font-light leading-relaxed max-w-3xl mx-auto">
              Personalized investment solutions designed to grow and preserve your wealth
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Comprehensive Wealth Management</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
              Our wealth management services are designed to address every aspect of your financial life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                data-aos="fade-up"
                data-aos-delay={`${200 + index * 150}`}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 p-8 border border-gray-100"
              >
                <div className="w-12 h-12 rounded-full bg-[#0056d7] flex items-center justify-center shadow-md mb-6">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                    <path d="M12 2l7 4-7 4-7-4 7-4z" />
                    <path d="M5 10l7 4 7-4" />
                    <path d="M5 18l7 4 7-4" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-[#0056d7] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Our Wealth Management Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
              A systematic approach to building and preserving your wealth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div
                key={step.step}
                data-aos="fade-up"
                data-aos-delay={`${200 + index * 100}`}
                className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105"
              >
                <div className="absolute -top-4 left-8 w-12 h-12 bg-[#0056d7] text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0056d7] via-[#003a9e] to-[#002a73] text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-light text-white mb-6">Ready to Secure Your Financial Future?</h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Schedule a confidential consultation with our wealth management experts
            </p>
            <motion.a
              href="/contact"
              className="inline-block px-12 py-6 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#d97706] text-black font-medium text-xl transition-all duration-500 shadow-2xl hover:shadow-amber-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule Consultation
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default WealthManagement;
